import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTrackLibrarySearch } from '@/hooks/analytics/useTrackLibrarySearch';
import { notify } from '@/lib/notify';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';
import { LearningPagination } from '@/components/features/learning/navigation/LearningPagination';
import { FilteredEmptyHint } from '@/components/features/learning/organization/FilteredEmptyHint';
import { LearningCardGridSkeleton } from '@/components/features/learning/organization/LearningCardGridSkeleton';
import { MyLearningDialogs } from '@/components/features/learning/organization/MyLearningDialogs';
import { MyLearningEmptyState } from '@/components/features/learning/organization/MyLearningEmptyState';
import { MyLearningGrid } from '@/components/features/learning/organization/MyLearningGrid';
import { MyLearningHeader } from '@/components/features/learning/organization/MyLearningHeader';
import type { ViewFilterValue } from '@/components/features/learning/organization/ViewFilter';
import type { EditLessonDetailsPayload } from '@/components/features/learning/organization/EditLessonDetailsModal';
import { useVersionList } from '@/hooks/lesson/useVersions';
import {
  lessonApi,
  folderApi,
  type FolderItem,
  type UserLessonItem,
} from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';

export function MyLearningPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // URL-driven state
  const folderIdFromUrl = searchParams.get('folder');
  const searchFromUrl = searchParams.get('search') || '';
  const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);

  const [searchValue, setSearchValue] = useState(searchFromUrl);
  const [debouncedSearch, setDebouncedSearch] = useState(searchFromUrl);
  const [currentPage, setCurrentPage] = useState(pageFromUrl);
  // Listing view filter - controls whether folders, lessons, or both
  // render in the grid. Defaults to "all" (mixed). Lives only in
  // component state for now; promote to URL params if we want
  // shareable filtered views.
  const [viewFilter, setViewFilter] = useState<ViewFilterValue>('all');
  // Tag filter (OR match). Same scope rationale as viewFilter.
  const [tagFilter, setTagFilter] = useState<string[]>([]);

  // Keep local state in sync when URL changes (e.g. back/forward or breadcrumb)
  useEffect(() => {
    setSearchValue(searchFromUrl);
    setDebouncedSearch(searchFromUrl);
    setCurrentPage(pageFromUrl);
  }, [folderIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Modal state
  const [deleteLessonId, setDeleteLessonId] = useState<string | null>(null);
  const [deleteFolder, setDeleteFolder] = useState<FolderItem | null>(null);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [renameFolderTarget, setRenameFolderTarget] =
    useState<FolderItem | null>(null);
  const [moveLessonTarget, setMoveLessonTarget] = useState<{
    lessonId: string;
    folderId: string | null;
  } | null>(null);
  // Edit-details modal target - title, description, and tags now flow
  // through one modal + one save (was tags-only).
  const [editDetailsTarget, setEditDetailsTarget] = useState<{
    lessonId: string;
    title: string;
    description: string;
    tags: string[];
  } | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Sync URL params
  useEffect(() => {
    const params: Record<string, string> = {};
    if (folderIdFromUrl) params.folder = folderIdFromUrl;
    if (debouncedSearch) params.search = debouncedSearch;
    if (currentPage > 1) params.page = currentPage.toString();
    setSearchParams(params, { replace: true });
  }, [folderIdFromUrl, debouncedSearch, currentPage, setSearchParams]);

  // ---- Data queries ------------------------------------------------------

  // Unified folder + lesson stream - `include_folders=true` makes the
  // backend interleave both types into one paginated `items` array
  // sorted by updated_at desc. Folders count toward pagination, so
  // deleting any item naturally shifts subsequent items up.
  //
  // We still fetch folders separately for one specific case: the
  // breadcrumbs / navigation surface uses subfolders to navigate
  // *into* this folder, but those aren't shown as cards (the
  // breadcrumbs query handles that). With include_folders the cards
  // grid gets everything it needs from the lessons endpoint.
  const lessonsQuery = useQuery({
    queryKey: queryKeys.lessons.list({
      search: debouncedSearch,
      page: currentPage,
      folder: folderIdFromUrl ?? 'root',
      includeFolders: true,
      itemType: viewFilter,
      tags: tagFilter,
    }),
    queryFn: () =>
      lessonApi.getUserLessons({
        search: debouncedSearch || undefined,
        page: currentPage,
        limit: 12,
        folderId: folderIdFromUrl ?? 'root',
        includeFolders: true,
        itemType: viewFilter,
        tags: tagFilter.length > 0 ? tagFilter : undefined,
      }),
    staleTime: 30000,
    // Auto-poll while any lesson on this page is still generating. As soon
    // as the overview lands the card title flips from "Generating…" to the
    // real title without the user having to refresh. Stops as soon as
    // every lesson is in a terminal state (completed/failed).
    refetchInterval: query => {
      const lessons = query.state.data?.lessons ?? [];
      const inFlight = lessons.some(
        l => l.status === 'pending' || l.status === 'processing'
      );
      return inFlight ? 3000 : false;
    },
  });

  // Breadcrumbs for the current folder
  const breadcrumbsQuery = useQuery({
    queryKey: ['folders', 'breadcrumbs', folderIdFromUrl],
    queryFn: () => folderApi.getBreadcrumbs(folderIdFromUrl!),
    enabled: !!folderIdFromUrl,
    staleTime: 60000,
  });

  const breadcrumbs = breadcrumbsQuery.data?.breadcrumbs ?? [];
  const lessonsData = lessonsQuery.data;

  const isLoading = lessonsQuery.isLoading;
  const error = lessonsQuery.error;
  // A failed read with nothing cached is a load failure, not an empty
  // library. A failed REFETCH keeps `data`, so the list stays on screen and
  // the toast below is the only notice.
  const loadFailed = !!error && !lessonsData;

  useEffect(() => {
    // Toast only a failed refetch: a failed cold load has its own error
    // state with Retry, and saying it twice is noise.
    if (error && lessonsData) {
      notify.error(
        (error as any).message || 'Failed to load. Please try again.'
      );
    }
  }, [error, lessonsData]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['folders'] });
    queryClient.invalidateQueries({ queryKey: queryKeys.lessons.all });
    // `lessons.all` covers `lessons.tags()` too (same prefix), but
    // call it out explicitly so future re-keying doesn't silently
    // break tag-option freshness after edits/deletes.
    queryClient.invalidateQueries({ queryKey: queryKeys.lessons.tags() });
  };

  // ---- Handlers ----------------------------------------------------------

  const navigateToFolder = (id: string | null) => {
    const params: Record<string, string> = {};
    if (id) params.folder = id;
    setSearchParams(params);
  };

  const clearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setCurrentPage(1);
  };

  const handleRetry = async (lesson: UserLessonItem) => {
    try {
      // Re-dispatch whatever blocking step failed - works for an errored
      // overview (OVERVIEW_GENERATION_FAILED), a stuck overview/byte_selection
      // (QUEUE_STUCK), or context (CONTEXT_GENERATION_FAILED) alike. Don't
      // hard-code the overview retry, which missed the stuck case.
      await lessonApi.retryAllFailed(lesson.lesson_id);
      notify.success('Retrying lesson generation...');
      lessonsQuery.refetch();
    } catch (err: any) {
      notify.error(err.message || 'Failed to retry lesson generation');
    }
  };

  const confirmDeleteLesson = async () => {
    if (!deleteLessonId) return;
    try {
      await lessonApi.deleteLesson(deleteLessonId);
      notify.success('Lesson deleted');
      invalidateAll();
    } catch (err: any) {
      notify.error(err.message || 'Failed to delete lesson');
    } finally {
      setDeleteLessonId(null);
    }
  };

  const confirmDeleteFolder = async () => {
    if (!deleteFolder) return;
    try {
      await folderApi.deleteFolder(deleteFolder.folder_id);
      notify.success(
        `Folder "${deleteFolder.name}" deleted (contents moved up one level)`
      );
      invalidateAll();
    } catch (err: any) {
      notify.error(err.message || 'Failed to delete folder');
    } finally {
      setDeleteFolder(null);
    }
  };

  const handleCreateFolder = async ({
    name,
    tags,
  }: {
    name: string;
    tags: string[];
  }) => {
    try {
      await folderApi.createFolder({
        name,
        tags,
        parent_folder_id: folderIdFromUrl,
      });
      notify.success(`Folder "${name}" created`);
      invalidateAll();
    } catch (err: any) {
      notify.error(err.message || 'Failed to create folder');
      throw err;
    }
  };

  const handleRenameFolder = async ({
    name,
    tags,
  }: {
    name: string;
    tags: string[];
  }) => {
    if (!renameFolderTarget) return;
    try {
      await folderApi.updateFolder(renameFolderTarget.folder_id, {
        name,
        tags,
      });
      notify.success('Folder updated');
      invalidateAll();
    } catch (err: any) {
      notify.error(err.message || 'Failed to update folder');
      throw err;
    }
  };

  const handleMoveLesson = async (folderId: string | null) => {
    if (!moveLessonTarget) return;
    try {
      await lessonApi.updateLesson(moveLessonTarget.lessonId, {
        folder_id: folderId,
      });
      notify.success('Lesson moved');
      invalidateAll();
    } catch (err: any) {
      notify.error(err.message || 'Failed to move lesson');
      throw err;
    }
  };

  const handleEditDetails = async (payload: EditLessonDetailsPayload) => {
    if (!editDetailsTarget) return;
    try {
      await lessonApi.updateLesson(editDetailsTarget.lessonId, payload);
      notify.success('Lesson updated');
      invalidateAll();
      // Title changes rewrite the H1 baked into the existing text +
      // mindmap artifacts on the backend; drop the cached copies so the
      // lesson page picks up the new heading on next visit.
      queryClient.invalidateQueries({
        queryKey: queryKeys.lessons.text(editDetailsTarget.lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.lessons.mindmap(editDetailsTarget.lessonId),
      });
      // Per-version snapshot edits also need the version list cache to
      // refresh so the modal repopulates with the new value if reopened.
      queryClient.invalidateQueries({
        queryKey: ['versions', 'list', editDetailsTarget.lessonId],
      });
    } catch (err: any) {
      notify.error(err.message || 'Failed to update lesson');
      throw err;
    }
  };

  // Fetch the version list for the lesson currently being edited so the
  // modal can render the version dropdown. The hook short-circuits when
  // `lessonId` is undefined (no fetch fires until the modal opens).
  const editVersionsQuery = useVersionList(editDetailsTarget?.lessonId);

  // ---- Derived values ----------------------------------------------------

  const isRoot = !folderIdFromUrl;
  // Total items on this page - folders + lessons that landed in the
  // unified stream slice. Used by the noContent check.
  const totalItems = lessonsData?.items.length ?? 0;
  const searchCount = isLoading ? undefined : lessonsData?.totalCount;
  const filtered = viewFilter !== 'all' || tagFilter.length > 0 || !isRoot;
  useTrackLibrarySearch(debouncedSearch, searchCount, filtered);
  // Empty means the read SUCCEEDED with zero items - never "it failed".
  const noContent =
    !isLoading && !loadFailed && (lessonsData?.totalCount ?? 0) === 0;

  const headerTitle = useMemo(() => {
    if (isRoot) return 'My Learning';
    return breadcrumbs[breadcrumbs.length - 1]?.name ?? 'Folder';
  }, [isRoot, breadcrumbs]);

  // Grid entries - already merged, sorted, AND filtered server-side
  // via the `item_type` query param. The frontend just renders.
  const gridItems = lessonsData?.items ?? [];

  // Which of the three "nothing here" stories applies. A live search wins
  // over folder context: the user's last action is the one to undo.
  const emptyVariant = debouncedSearch ? 'search' : isRoot ? 'root' : 'folder';

  // ---- Render ------------------------------------------------------------

  return (
    // AppLayout's <main> is `h-full overflow-hidden`. We want the
    // header + search row to stay fixed at the top and only the cards
    // area to scroll - so this page is a flex column that fills the
    // available height, with a scroll container wrapping just the grid.
    <div className='mx-auto flex h-full max-w-7xl flex-col px-4 py-4 sm:px-6 md:py-8 lg:px-8'>
      <MyLearningHeader
        title={headerTitle}
        isRoot={isRoot}
        breadcrumbs={breadcrumbs}
        onNavigateFolder={navigateToFolder}
        search={searchValue}
        onSearchChange={setSearchValue}
        onSearchClear={clearSearch}
        totalResults={lessonsData?.totalCount}
        isLoading={isLoading}
        viewFilter={viewFilter}
        onViewFilterChange={next => {
          // Reset to page 1 on filter change - the new filter produces a
          // different total count, so staying on (e.g.) page 3 of
          // folders-only might be out of bounds.
          setViewFilter(next);
          setCurrentPage(1);
        }}
        tagFilter={tagFilter}
        onTagFilterChange={next => {
          setTagFilter(next);
          setCurrentPage(1);
        }}
        onNewFolder={() => setCreateOpen(true)}
      />

      {/* Scroll container - only the grid + pagination scroll; the
          header + search row above stay pinned. `min-h-0` is required
          on flex-1 children to actually shrink instead of expanding
          their parent.

          Internal padding (px-1.5 / py-1.5) gives cards breathing room
          inside the container so the `hover:scale-[1.02]` on each
          LearningCard doesn't clip against the scroll edges (CSS
          spec: `overflow-y: auto` forces overflow-x to also clip,
          so the only fix is padding inside the scroll). */}
      <div
        data-my-learning-scroll
        className='min-h-0 flex-1 overflow-y-auto px-1.5 py-1.5'
      >
        {isLoading && <LearningCardGridSkeleton />}

        {loadFailed && (
          // PanelFallback, not ErrorState: no `role='alert'` of its own, so
          // it cannot compete with the shell's service-issues strip, which
          // already announces a 5xx (and the offline strip a dropped link).
          <PanelFallback
            titleAs='h2'
            title="Couldn't load your library"
            description='Check your connection and try again. Your lessons are safe.'
            primary={{
              label: 'Retry',
              onClick: () => void lessonsQuery.refetch(),
              busy: lessonsQuery.isFetching,
            }}
          />
        )}

        {noContent && (
          <MyLearningEmptyState
            variant={emptyVariant}
            searchTerm={debouncedSearch}
            onClearSearch={clearSearch}
            onNewSubfolder={() => setCreateOpen(true)}
            onNewLesson={() =>
              navigate(folderIdFromUrl ? `/?folder=${folderIdFromUrl}` : '/')
            }
          />
        )}

        {/* When the user has content but the active filter excludes
            everything on this page, show a small empty hint rather
            than a blank container. */}
        {!isLoading && totalItems > 0 && gridItems.length === 0 && (
          <FilteredEmptyHint viewFilter={viewFilter} />
        )}

        {!isLoading && totalItems > 0 && gridItems.length > 0 && (
          <>
            <MyLearningGrid
              items={gridItems}
              onOpenFolder={f => navigateToFolder(f.folder_id)}
              onRenameFolder={setRenameFolderTarget}
              onDeleteFolder={setDeleteFolder}
              onContinueLesson={l => navigate(`/my-learning/${l.lesson_id}`)}
              onRetryLesson={handleRetry}
              onDeleteLesson={l => setDeleteLessonId(l.lesson_id)}
              onMoveLesson={l =>
                setMoveLessonTarget({
                  lessonId: l.lesson_id,
                  folderId: l.folder_id,
                })
              }
              onEditLessonDetails={l =>
                setEditDetailsTarget({
                  lessonId: l.lesson_id,
                  title: l.title,
                  description: l.description ?? '',
                  tags: l.tags,
                })
              }
            />

            {/* Pagination spans the full unified stream (folders +
              lessons). Deleting any item shifts subsequent cards
              naturally. */}
            {lessonsData && lessonsData.totalPages > 1 && (
              <LearningPagination
                currentPage={currentPage}
                totalPages={lessonsData.totalPages}
                totalItems={lessonsData.totalCount}
                itemsPerPage={12}
                onPageChange={page => {
                  setCurrentPage(page);
                  // Scroll the cards container back to the top, not
                  // the window - the header is pinned outside this
                  // scrolly box.
                  document
                    .querySelector('[data-my-learning-scroll]')
                    ?.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            )}
          </>
        )}
      </div>

      <MyLearningDialogs
        deleteLesson={{
          isOpen: !!deleteLessonId,
          onClose: () => setDeleteLessonId(null),
          onConfirm: confirmDeleteLesson,
        }}
        deleteFolder={{
          isOpen: !!deleteFolder,
          folderName: deleteFolder?.name ?? '',
          onClose: () => setDeleteFolder(null),
          onConfirm: confirmDeleteFolder,
        }}
        createFolder={{
          isOpen: isCreateOpen,
          isRoot,
          onClose: () => setCreateOpen(false),
          onSubmit: handleCreateFolder,
        }}
        renameFolder={{
          isOpen: !!renameFolderTarget,
          initial: renameFolderTarget
            ? { name: renameFolderTarget.name, tags: renameFolderTarget.tags }
            : undefined,
          onClose: () => setRenameFolderTarget(null),
          onSubmit: handleRenameFolder,
        }}
        moveLesson={{
          isOpen: !!moveLessonTarget,
          currentFolderId: moveLessonTarget?.folderId ?? null,
          onClose: () => setMoveLessonTarget(null),
          onSelect: handleMoveLesson,
        }}
        editDetails={{
          isOpen: !!editDetailsTarget,
          initialTitle: editDetailsTarget?.title ?? '',
          initialDescription: editDetailsTarget?.description ?? '',
          initialTags: editDetailsTarget?.tags ?? [],
          versions: editVersionsQuery.data?.versions,
          onClose: () => setEditDetailsTarget(null),
          onSubmit: handleEditDetails,
        }}
      />
    </div>
  );
}
